import { useEffect } from "react";

interface SeoMetaOptions {
  title: string;
  description: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  keywords?: string;
}

function setMetaTag(name: string, content: string, property = false) {
  const attr = property ? "property" : "name";
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLinkTag(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Dynamically updates <head> SEO meta tags.
 * Restores the original index.html values on unmount so the homepage
 * is not left with stale tags when navigating back.
 */
export function useSeoMeta(options: SeoMetaOptions) {
  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]')?.getAttribute("content") ?? "";
    const prevCanonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.getAttribute("href") ?? "";
    const prevOgTitle = document.querySelector<HTMLMetaElement>('meta[property="og:title"]')?.getAttribute("content") ?? "";
    const prevOgDesc = document.querySelector<HTMLMetaElement>('meta[property="og:description"]')?.getAttribute("content") ?? "";

    document.title = options.title;
    setMetaTag("description", options.description);
    setLinkTag("canonical", options.canonical);
    setMetaTag("og:title", options.ogTitle ?? options.title, true);
    setMetaTag("og:description", options.ogDescription ?? options.description, true);
    setMetaTag("og:type", options.ogType ?? "website", true);
    setMetaTag("og:url", options.canonical, true);
    if (options.ogImage) setMetaTag("og:image", options.ogImage, true);
    if (options.keywords) setMetaTag("keywords", options.keywords);
    setMetaTag("twitter:title", options.ogTitle ?? options.title);
    setMetaTag("twitter:description", options.ogDescription ?? options.description);
    setMetaTag("last-modified", new Date().toISOString().split("T")[0]);

    return () => {
      document.title = prevTitle;
      setMetaTag("description", prevDesc);
      setLinkTag("canonical", prevCanonical);
      setMetaTag("og:title", prevOgTitle, true);
      setMetaTag("og:description", prevOgDesc, true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.title, options.description, options.canonical]);
}
